import React from "react";
import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  CircularProgress,
  TextField,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { Button as Btn } from "../../../components/atoms/Button";
import theme from "../../../theme";
import { FlexRow, Spacer } from "../../../components";
import { useSnackbar } from "../../../context/SnackbarContext";
import type { ColorsCodeType } from "../../../typings";
import { MdComment } from "react-icons/md";
import api from "../../../api/axiosInstance";
import * as yup from "yup";

interface CommentModalProps {
  open: boolean;
  onClose: () => void;
  scholarshipApplicationId: number;
  onRefresh: () => void;
}

const commentSchema = yup.object().shape({
  comment: yup.string().trim().required("Comment is required"),
});

export default function CommentModal({
  open,
  onClose,
  scholarshipApplicationId,
  onRefresh,
}: CommentModalProps) {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState("");
  const [isResubmissionAllowed, setIsResubmissionAllowed] = useState(false);
  const [errors, setErrors] = useState<{ comment?: string }>({});

  const handleSubmit = async () => {
    try {
      // Validate form
      await commentSchema.validate({ comment }, { abortEarly: false });
      setErrors({});

      setLoading(true);
      await api.post("/scholarship-applications/comment/", {
        scholarship_application_id: scholarshipApplicationId,
        comment,
        is_resubmission_allowed: isResubmissionAllowed,
      });

      showSnackbar("Comment added successfully", "success");

      // Refresh the applications list
      onRefresh();

      // Reset form and close modal after successful submission
      setComment("");
      setIsResubmissionAllowed(false);
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        // Handle validation errors
        const validationErrors: { [key: string]: string } = {};
        error.inner.forEach((err) => {
          if (err.path) {
            validationErrors[err.path] = err.message;
          }
        });
        setErrors(validationErrors);

        showSnackbar("Please fill in all required fields", "warning");
      } else {
        // Handle API errors
        console.error("Error adding comment:", error);
        showSnackbar("Failed to add comment. Please try again.", "error");
      }

      setLoading(false);
    }
  };



  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          style: {
            borderRadius: "6px",
            maxWidth: "550px",
            width: "500px",
          },
        }}
      >
        <DialogTitle
          style={{
            padding: "24px 24px 16px",
            fontSize: "24px",
            fontWeight: 600,
          }}
        >
          <FlexRow align="center">
            <MdComment
              size={24}
              style={{
                marginRight: "10px",
                color: theme.palette.primary.main,
              }}
            />
            Add Comment
          </FlexRow>
        </DialogTitle>
        <DialogContent style={{ padding: "0px 24px 16px" }}>
          <Typography
            variant="body2"
            style={{
              marginBottom: "20px",
              color: theme.palette.colors.gray['500'],
            }}
          >
            Add your comment to this scholarship application and specify if
            resubmission is allowed.
          </Typography>

          <TextField
            label="Comment"
            multiline
            rows={4}
            value={comment}
            onChange={(e) => {
              setComment(e.target.value);
              // Clear error when user types
              if (errors.comment) {
                setErrors({ ...errors, comment: undefined });
              }
            }}
            fullWidth
            variant="outlined"
            placeholder="Enter your comment here..."
            error={!!errors.comment}
            helperText={errors.comment}
            sx={{
              marginBottom: "16px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "4px",
              },
            }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={isResubmissionAllowed}
                onChange={(e) => setIsResubmissionAllowed(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography
                variant="body2"
              >
                Allow resubmission
              </Typography>
            }
          />
        </DialogContent>
        <FlexRow
          justify="flex-end"
          align="center"
          style={{ padding: "15px 24px 24px" }}
        >
          <Btn
            buttonColor={theme.palette.colors["black"]["600"] as ColorsCodeType}
            textColor={theme.palette.colors["white"]["400"] as ColorsCodeType}
            onClick={onClose}
          >
            Cancel
          </Btn>
          <Spacer horizontal={1} />
          <Btn onClick={handleSubmit} disabled={loading || !comment.trim()}>
            {loading ? (
              <>
                <CircularProgress
                  size={16}
                  style={{
                    marginRight: 8,
                    color: theme.palette.colors.white[400] as string,
                  }}
                />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Btn>
        </FlexRow>
      </Dialog>

    </>
  );
}
